/**
 * OAuth Callback Page
 *
 * Handles the redirect from Google/Microsoft OAuth.
 * Processes the token and redirects to 2FA if required.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import authService, { postLoginRedirect } from '../../services/authService.js';

function OAuthCallback() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(null);
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processCallback = async () => {
            try {
                // Check for error from OAuth provider
                const errorParam = searchParams.get('error');
                if (errorParam) {
                    const errorDescription = searchParams.get('error_description') || errorParam;
                    throw new Error(errorDescription);
                }

                // Get token from URL
                const token = searchParams.get('token');
                if (!token) {
                    throw new Error('No authentication token received');
                }

                // Process the callback
                const result = await authService.handleOAuthCallback(token);

                if (result.requiresTwoFactor) {
                    // Redirect to 2FA verification
                    navigate('/verify-2fa', { replace: true });
                } else {
                    // Login complete - redirect to the session's destination.
                    const session = authService.getSession();
                    const portalType = session?.type || 'staff';

                    // GTA session: EVERY axis — district, vendor, and staff — lands on /gta,
                    // which dispatches by axis to its landing surface (district home / vendor
                    // console / staff ops) via landingPath. The GTA env has its own identity +
                    // surfaces — never the platform district/staff portals. Routed through the
                    // shared postLoginRedirect resolver so no axis is ever missed here again.
                    if (session?.gta) {
                        navigate(postLoginRedirect(session), { replace: true });
                        return;
                    }

                    // Bridge vendor auth: also populate vendorAuthStore so vendor pages recognize the session
                    if (portalType === 'vendor') {
                        const { useVendorAuthStore } = await import('../../../../shared/stores/vendorAuthStore');
                        useVendorAuthStore.getState().setVendor(
                            {
                                vendor_id: session?.userId || '',
                                company_name: session?.name || 'Vendor',
                                contact_email: session?.email || '',
                                status: 'active',
                            },
                            token
                        );
                    }

                    navigate(postLoginRedirect(session), { replace: true });
                }
            } catch (err) {
                console.error('OAuth callback error:', err);
                setError(err.message || 'Authentication failed');
                setProcessing(false);
            }
        };

        processCallback();
    }, [navigate, searchParams]);

    if (error) {
        return (
            <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                    <h1 className="text-xl font-semibold text-slate-900 mb-2">Authentication Failed</h1>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login', { replace: true })}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Return to Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center">
                <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <h1 className="text-xl font-semibold text-slate-900 mb-2">
                    {processing ? 'Completing sign in...' : 'Redirecting...'}
                </h1>
                <p className="text-slate-600">Please wait while we verify your credentials.</p>
            </div>
        </div>
    );
}

export default OAuthCallback;
